import { useEffect, useState } from 'react'
import { db } from '@/runtime'
import { META } from '@/db/schema'
import { Button, Sheet, cx } from './ui'
import { ICON_PREFIX, Icon, LINE_ICONS } from '@/lib/icons'

const SETS: [string, string[]][] = [
  ['Home', ['🏠', '🧹', '🧺', '🍳', '🛒', '🪴', '🐶', '🐱', '🔧', '📦', '🛏️', '🚿']],
  ['Work', ['💼', '💻', '📧', '📞', '📊', '📝', '🗓️', '🎯', '🚀', '🧠', '💡', '🧾']],
  ['Dev', ['🛠️', '🐛', '🔀', '🧪', '📦', '☸️', '🐳', '🔐', '📡', '🧱', '⚙️', '🤖']],
  ['Health', ['🦷', '💊', '🏃', '🧘', '🥗', '💤', '❤️', '🩺', '🚴', '🏋️', '👓', '🧴']],
  ['Life', ['🎉', '🎁', '✈️', '🎸', '📚', '🎬', '🌳', '☕', '🍕', '💸', '🏦', '🚗']],
  ['Marks', ['⭐', '🔥', '✅', '❗', '❓', '📌', '🔁', '⏳', '🧭', '🎨', '🔵', '🟣']],
]

/** Recently used first, curated sets, and a free field so the OS keyboard works on the phone. */
export const EmojiPicker = ({
  open,
  onClose,
  onPick,
  value,
}: {
  open: boolean
  onClose: () => void
  onPick: (emoji: string | null) => void
  value?: string
}) => {
  const [recent, setRecent] = useState<string[]>([])
  const [custom, setCustom] = useState('')
  const [tab, setTab] = useState<'emoji' | 'icons'>(() =>
    value?.startsWith(ICON_PREFIX) ? 'icons' : 'emoji',
  )
  const [q, setQ] = useState('')
  useEffect(() => {
    if (open) void db.getMeta<string[]>(META.recentEmoji).then((r) => setRecent(r ?? []))
  }, [open])
  const pick = async (e: string | null) => {
    if (e) {
      const next = [e, ...recent.filter((x) => x !== e)].slice(0, 16)
      await db.setMeta(META.recentEmoji, next)
    }
    onPick(e)
    onClose()
  }
  const grid = (list: string[]) => (
    <div className="grid grid-cols-8 gap-1">
      {list.map((e) => (
        <button
          key={e}
          type="button"
          onClick={() => void pick(e)}
          className={cx(
            'h-10 rounded-md text-xl hover:bg-raised flex items-center justify-center',
            value === e && 'bg-accent-soft ring-1 ring-accent',
          )}
          aria-label={e}
        >
          <Icon value={e} size={22} />
        </button>
      ))}
    </div>
  )
  const needle = q.trim().toLowerCase()
  const iconNames = Object.keys(LINE_ICONS).filter((n) => !needle || n.includes(needle))
  return (
    <Sheet open={open} onClose={onClose} title="Icon">
      <div className="flex gap-1 mb-3" role="tablist">
        <Button
          variant={tab === 'emoji' ? 'chipOn' : 'chip'}
          onClick={() => setTab('emoji')}
          data-testid="icon-tab-emoji"
        >
          Emoji
        </Button>
        <Button
          variant={tab === 'icons' ? 'chipOn' : 'chip'}
          onClick={() => setTab('icons')}
          data-testid="icon-tab-icons"
        >
          Icons
        </Button>
        <span className="flex-1" />
        {value && (
          <Button onClick={() => void pick(null)} aria-label="Remove icon">
            None
          </Button>
        )}
      </div>
      {tab === 'icons' && (
        <>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search icons"
            className="w-full mb-3"
            aria-label="Search icons"
            autoFocus
          />
          {grid(iconNames.map((n) => ICON_PREFIX + n))}
          {iconNames.length === 0 && <p className="text-sm text-faint">No icon matches.</p>}
        </>
      )}
      <div className={cx('flex gap-2 mb-3', tab !== 'emoji' && 'hidden')}>
        <input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          placeholder="Type or paste any emoji"
          className="flex-1"
          aria-label="Custom emoji"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && custom.trim()) void pick(custom.trim().slice(0, 8))
          }}
        />
        <Button
          variant="primary"
          disabled={!custom.trim()}
          onClick={() => void pick(custom.trim().slice(0, 8))}
        >
          Use
        </Button>
      </div>
      {tab === 'emoji' && recent.length > 0 && (
        <section className="mb-3">
          <h3 className="text-[13px] font-semibold text-muted mb-1">Recent</h3>
          {grid(recent)}
        </section>
      )}
      {tab === 'emoji' &&
        SETS.map(([name, list]) => (
          <section key={name} className="mb-3">
            <h3 className="text-[13px] font-semibold text-muted mb-1">{name}</h3>
            {grid(list)}
          </section>
        ))}
    </Sheet>
  )
}

/** A button showing the current icon that opens the picker. */
export const IconButtonField = ({
  value,
  onChange,
  label = 'Icon',
}: {
  value?: string
  onChange: (v: string | undefined) => void
  label?: string
}) => {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="h-11 w-11 rounded-md bg-panel border border-line text-2xl flex items-center justify-center hover:bg-raised"
        aria-label={label}
        data-testid="icon-field"
      >
        {value ? <Icon value={value} size={22} /> : <span className="text-faint text-sm">＋</span>}
      </button>
      <EmojiPicker
        open={open}
        onClose={() => setOpen(false)}
        value={value}
        onPick={(e) => onChange(e ?? undefined)}
      />
    </>
  )
}
